import { useState, useRef, useEffect } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { initiateLogout } from '../utils/oauth';
import { config } from '../config/env';

function UserIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function ChevronIcon({ isOpen }: { isOpen: boolean }) {
  return (
    <svg
      className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  );
}

function DatabaseIcon() {
  return (
    <svg
      className="w-4 h-4 text-gray-500 dark:text-gray-400"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"
      />
    </svg>
  );
}

function SignOutIcon() {
  return (
    <svg
      className="w-4 h-4 text-gray-500 dark:text-gray-400"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9"
      />
    </svg>
  );
}

export function UserMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuthContext();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayName = user?.email || user?.sub || 'User';

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={
          'flex items-center gap-2.5 pl-3 pr-2.5 py-1.5 text-sm font-medium rounded-lg transition-all duration-150 text-white/95 border border-white/20 hover:border-white/30 hover:bg-white/10 ' +
          (isOpen ? 'bg-white/15' : 'bg-white/5')
        }
      >
        <UserIcon />
        <span className="max-w-[180px] truncate">{displayName}</span>
        <ChevronIcon isOpen={isOpen} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-52 rounded-lg shadow-xl overflow-hidden z-50 bg-white border border-gray-200 dark:bg-gray-900 dark:border-gray-700">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 dark:bg-gray-950 dark:border-gray-800">
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Signed in as
            </p>
            <p className="text-sm font-medium truncate mt-0.5 text-gray-900 dark:text-gray-100">
              {displayName}
            </p>
          </div>

          <div className="py-1.5">
            <a
              href={`${config.apiBaseUrl}/schema.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-2.5 text-sm no-underline text-ink hover:bg-brand-50 dark:text-gray-200 dark:hover:bg-gray-800 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <DatabaseIcon />
              <span>Database Schema</span>
            </a>

            <div className="my-1.5 mx-3 border-t border-gray-100 dark:border-gray-800" />

            <button
              onClick={async () => {
                setIsOpen(false);
                await initiateLogout();
              }}
              className="flex items-center gap-3 px-4 py-2.5 text-sm w-full text-left text-ink hover:bg-brand-50 dark:text-gray-200 dark:hover:bg-gray-800 transition-colors"
            >
              <SignOutIcon />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
